import { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  areas,
  schools,
  getSchoolColor,
  getDistanceToAssigned,
  getHeatmapColor,
} from './data';
import type { MapState } from './types';

interface MapViewProps {
  state: MapState;
  onAreaClick: (areaId: string) => void;
  onSchoolClick: (schoolName: string) => void;
}

const SCHOOL_ICON_SIZE = 28;

function createSchoolIcon(
  color: string,
  isOpen: boolean,
  name: string,
): L.DivIcon {
  const bg = isOpen ? color : '#808080';
  const label = name.replace(' ES', '');
  return L.divIcon({
    className: '',
    iconSize: [SCHOOL_ICON_SIZE, SCHOOL_ICON_SIZE + 8],
    iconAnchor: [SCHOOL_ICON_SIZE / 2, SCHOOL_ICON_SIZE + 8],
    popupAnchor: [0, -(SCHOOL_ICON_SIZE + 8)],
    html: `<div style="position:relative;width:${SCHOOL_ICON_SIZE}px;height:${SCHOOL_ICON_SIZE + 8}px;">
      <div style="position:absolute;bottom:100%;left:50%;transform:translateX(-50%);white-space:nowrap;font-size:11px;font-weight:700;color:${bg};text-shadow:-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff,0 0 4px #fff;padding-bottom:2px;pointer-events:none">${label}</div>
      <svg viewBox="0 0 24 32" width="${SCHOOL_ICON_SIZE}" height="${SCHOOL_ICON_SIZE + 8}">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${bg}" stroke="#fff" stroke-width="1.5"/>
        <circle cx="12" cy="11" r="5" fill="white" opacity="0.9"/>
        <text x="12" y="14" text-anchor="middle" font-size="8" font-weight="bold" fill="${bg}">${isOpen ? 'S' : 'X'}</text>
      </svg>
    </div>`,
  });
}

export function MapView({ state, onAreaClick, onSchoolClick }: MapViewProps) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const polygonLayerRef = useRef<L.LayerGroup>(new L.LayerGroup());
  const markerLayerRef = useRef<L.LayerGroup>(new L.LayerGroup());
  const labelLayerRef = useRef<L.LayerGroup>(new L.LayerGroup());

  // Store callbacks in refs to avoid stale closures
  const onAreaClickRef = useRef(onAreaClick);
  const onSchoolClickRef = useRef(onSchoolClick);
  onAreaClickRef.current = onAreaClick;
  onSchoolClickRef.current = onSchoolClick;

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [38.555, -121.73],
      zoom: 14,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    polygonLayerRef.current.addTo(map);
    markerLayerRef.current.addTo(map);
    labelLayerRef.current.addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Get polygon color based on view mode
  const getPolygonColor = useCallback(
    (areaId: string) => {
      const area = areas.find((a) => a.id === areaId);
      if (!area) return '#cccccc';

      const assignedSchool = state.areaAssignments[areaId];
      if (!assignedSchool) return '#cccccc';

      if (state.viewMode === 'heatmap') {
        const dist = getDistanceToAssigned(area, assignedSchool);
        return getHeatmapColor(dist);
      }

      return getSchoolColor(assignedSchool);
    },
    [state.areaAssignments, state.viewMode],
  );

  // Update polygons
  useEffect(() => {
    polygonLayerRef.current.clearLayers();
    labelLayerRef.current.clearLayers();

    for (const area of areas) {
      const color = getPolygonColor(area.id);

      // Convert GeoJSON [lon, lat] to Leaflet [lat, lon]
      const latLngs = area.geometry.coordinates[0].map(
        (coord) => [coord[1], coord[0]] as L.LatLngTuple,
      );

      const polygon = L.polygon(latLngs, {
        color: '#555',
        weight: 1,
        fillColor: color,
        fillOpacity: state.viewMode === 'heatmap' ? 0.75 : 0.5,
        opacity: 0.7,
      });

      const assignedSchool = state.areaAssignments[area.id] || area.zone;
      const pop = area.population[state.selectedYear] ?? 0;
      const dist = getDistanceToAssigned(area, assignedSchool);

      polygon.bindTooltip(
        `<strong>${area.id}</strong><br/>` +
          `School: ${assignedSchool}<br/>` +
          `Pop (${state.selectedYear}): ${pop}<br/>` +
          `Distance: ${dist.toFixed(2)} mi`,
        { sticky: true, className: 'area-tooltip' },
      );

      polygon.on('click', () => onAreaClickRef.current(area.id));

      polygonLayerRef.current.addLayer(polygon);

      // Add population label at centroid
      if (area.centroid.lat && area.centroid.lon) {
        const label = L.marker([area.centroid.lat, area.centroid.lon], {
          icon: L.divIcon({
            className: '',
            html: `<div style="font-size:10px;font-weight:600;color:#333;text-shadow:0 0 3px white,0 0 3px white,0 0 3px white;white-space:nowrap;pointer-events:none;text-align:center;transform:translate(-50%,-50%)">${Math.round(pop)}</div>`,
            iconSize: [0, 0],
          }),
          interactive: false,
        });
        labelLayerRef.current.addLayer(label);
      }
    }
  }, [state.areaAssignments, state.viewMode, state.selectedYear, getPolygonColor]);

  // Update school markers
  useEffect(() => {
    markerLayerRef.current.clearLayers();

    for (const school of schools) {
      const isOpen = state.schoolStatus[school.name] ?? true;
      const color = getSchoolColor(school.name);
      const icon = createSchoolIcon(color, isOpen, school.name);

      const marker = L.marker([school.lat, school.lon], {
        icon,
        zIndexOffset: 1000,
      });

      marker.bindTooltip(
        `<strong>${school.name}</strong><br/>${isOpen ? 'Open' : 'Closed'}<br/>Click to toggle`,
        { direction: 'top' },
      );

      marker.on('click', () => onSchoolClickRef.current(school.name));

      markerLayerRef.current.addLayer(marker);
    }
  }, [state.schoolStatus]);

  // Resize handler
  useEffect(() => {
    const handleResize = () => mapRef.current?.invalidateSize();
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(handleResize, 100);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  return <div ref={containerRef} className="h-full w-full" />;
}
